import React, { useEffect, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import {
  CAMPAIGN_OPTIONS,
  CHILDREN_AGE_OPTIONS,
  DAD_STAGE_OPTIONS,
  MEMBER_TIER_OPTIONS,
  PRESSURE_AREA_OPTIONS,
  SOURCE_OPTIONS,
  WARMTH_OPTIONS,
} from './memberConstants';

const AddMemberModal = ({
  open,
  newMember,
  setNewMember,
  newMemberPhotoFile,
  setNewMemberPhotoFile,
  adding,
  onSubmit,
  onClose,
}) => {
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');

  useEffect(() => {
    if (!newMemberPhotoFile) {
      setPhotoPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(newMemberPhotoFile);
    setPhotoPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [newMemberPhotoFile]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="flex min-h-full items-start justify-center pt-8">
        <Card className="max-h-[calc(100vh-4rem)] w-full max-w-md overflow-y-auto border-[#2a2a32] bg-[#1e1e24] p-6">
          <h3 className="mb-4 text-lg font-semibold text-white">Add New Dad</h3>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm text-gray-400">Headshot (optional)</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="h-16 w-16 border-2 border-[#2a2a32]">
                    {photoPreviewUrl ? (
                      <AvatarImage src={photoPreviewUrl} alt="Preview" className="object-cover" />
                    ) : null}
                    <AvatarFallback className="bg-aqua/20 text-2xl text-aqua">
                      {newMember.name ? newMember.name.charAt(0).toUpperCase() : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <input
                    id="add-member-photo"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) setNewMemberPhotoFile(file);
                      event.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full border-[#2a2a32] bg-[#16161a] p-0 text-white hover:bg-[#252530]"
                    onClick={() => document.getElementById('add-member-photo')?.click()}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
                <div>
                  {newMemberPhotoFile ? (
                    <div className="flex items-center gap-2">
                      <span className="max-w-[140px] truncate text-sm text-gray-400">{newMemberPhotoFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setNewMemberPhotoFile(null)}
                        className="text-sm text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">Click camera to add a photo</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-400">Name *</label>
              <Input
                placeholder="Full name"
                value={newMember.name}
                onChange={(event) => setNewMember({ ...newMember, name: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400">Email *</label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={newMember.email}
                onChange={(event) => setNewMember({ ...newMember, email: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400">Phone</label>
              <Input
                type="tel"
                placeholder="Phone number"
                value={newMember.phone}
                onChange={(event) => setNewMember({ ...newMember, phone: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400">Relationship stage</label>
              <select
                value={newMember.relationship_stage}
                onChange={(event) => setNewMember({ ...newMember, relationship_stage: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {DAD_STAGE_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Tier</label>
              <select
                value={newMember.tier}
                onChange={(event) => setNewMember({ ...newMember, tier: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {MEMBER_TIER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Status</label>
              <select
                value={newMember.status}
                onChange={(event) => setNewMember({ ...newMember, status: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                <option value="active">Active</option>
                <option value="prospect">Prospect</option>
                <option value="former">Former</option>
                <option value="trial">Trial</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Billing cycle</label>
              <select
                value={newMember.billing_cycle}
                onChange={(event) => setNewMember({ ...newMember, billing_cycle: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Campaign</label>
              <select
                value={newMember.campaign_type}
                onChange={(event) => setNewMember({ ...newMember, campaign_type: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {CAMPAIGN_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Source</label>
              <select
                value={newMember.source}
                onChange={(event) => setNewMember({ ...newMember, source: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Warmth</label>
              <select
                value={newMember.warmth}
                onChange={(event) => setNewMember({ ...newMember, warmth: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {WARMTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Children ages</label>
              <select
                value={newMember.children_age_range}
                onChange={(event) => setNewMember({ ...newMember, children_age_range: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {CHILDREN_AGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Biggest pressure area</label>
              <select
                value={newMember.biggest_pressure_area}
                onChange={(event) => setNewMember({ ...newMember, biggest_pressure_area: event.target.value })}
                className="mt-1 w-full rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
              >
                {PRESSURE_AREA_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-400">Cohorts (comma-separated)</label>
              <Input
                placeholder="e.g. 2024-Q1, 2024-Q2"
                value={newMember.cohorts}
                onChange={(event) => setNewMember({ ...newMember, cohorts: event.target.value })}
                className="mt-1 border-[#2a2a32] bg-[#16161a] text-white"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400">Notes</label>
              <Textarea
                placeholder="Helpful background, referral context, or webinar notes"
                value={newMember.notes}
                onChange={(event) => setNewMember({ ...newMember, notes: event.target.value })}
                className="mt-1 min-h-[80px] border-[#2a2a32] bg-[#16161a] text-white"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-[#2a2a32] text-white hover:bg-[#16161a]"
              >
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                disabled={adding}
                className="flex-1 bg-aqua text-charcoal hover:bg-aqua-dark"
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Dad'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AddMemberModal;
